import { Radio, Tag } from 'antd';
import React, { useEffect, useState } from 'react';
import { DATE_FORMATS } from '../../common/constants';
import { formatDate } from '../../common/utils';
import CommonTable from '../CommonTable';
import ModalComponent from '../ModalComponent';

const formatDateTime = (value) =>
  value ? formatDate(value, DATE_FORMATS?.WITH_TIME) : '-';

const formatChecklistValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value?.join(', ') || '-';
  return `${value}`;
};

const isAnswerAsExpected = (item) => {
  const { expected, currentValue } = item || {};
  if (expected === null || expected === undefined) return true;
  if (currentValue === null || currentValue === undefined) return false;
  if (Array.isArray(expected)) return expected?.includes(currentValue);
  return expected === currentValue;
};

const contentsColumns = [
  {
    title: 'NAME',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    render: (name) => name || '-',
  },
  {
    title: 'DESCRIPTION',
    dataIndex: 'description',
    key: 'description',
    width: 220,
    render: (description) => description || '-',
  },
  {
    title: 'PRESENT QTY',
    dataIndex: 'currentQty',
    key: 'currentQty',
    width: 130,
    render: (_, record) => {
      const current = record?.currentQty;
      if (current === null || current === undefined) return '-';
      const isShort =
        record?.minimumQuantity !== null &&
        record?.minimumQuantity !== undefined &&
        current < record?.minimumQuantity;
      return (
        <Tag color={isShort ? 'error' : 'success'}>
          {current}
          {isShort ? ' (short)' : ''}
        </Tag>
      );
    },
  },
  {
    title: 'REQUIRED',
    dataIndex: 'minimumQuantity',
    key: 'minimumQuantity',
    width: 140,
    render: (_, record) => {
      const min = record?.minimumQuantity ?? '-';
      const max = record?.maximumQuantity ?? '-';
      return `${min} of ${max}`;
    },
  },
  {
    title: 'LAST CHECKED',
    dataIndex: 'lastCheckedAt',
    key: 'lastCheckedAt',
    width: 190,
    render: (lastCheckedAt) => formatDateTime(lastCheckedAt),
  },
];

const checklistColumns = [
  {
    title: 'QUESTION',
    dataIndex: 'text',
    key: 'text',
    width: 260,
    render: (text) => text || '-',
  },
  {
    title: 'EXPECTED',
    dataIndex: 'expected',
    key: 'expected',
    width: 150,
    render: (expected) => formatChecklistValue(expected),
  },
  {
    title: 'ANSWER',
    dataIndex: 'currentValue',
    key: 'currentValue',
    width: 150,
    render: (_, record) => {
      if (record?.currentValue === null || record?.currentValue === undefined) {
        return '-';
      }
      return (
        <Tag color={isAnswerAsExpected(record) ? 'success' : 'error'}>
          {formatChecklistValue(record?.currentValue)}
        </Tag>
      );
    },
  },
  {
    title: 'BLOCKING',
    dataIndex: 'isBlocking',
    key: 'isBlocking',
    width: 110,
    render: (isBlocking) => (isBlocking ? 'Yes' : 'No'),
  },
  {
    title: 'LAST CHECKED',
    dataIndex: 'lastCheckedAt',
    key: 'lastCheckedAt',
    width: 190,
    render: (lastCheckedAt) => formatDateTime(lastCheckedAt),
  },
];

const CONTENTS_MODE = 'contents';
const CHECKLIST_MODE = 'checklist';

const InventoryUnitContentsModal = ({ open, unit, mode, onClose, isDesktop }) => {
  const [activeMode, setActiveMode] = useState(mode || CONTENTS_MODE);

  useEffect(() => {
    setActiveMode(mode || CONTENTS_MODE);
  }, [mode, unit?.id]);
  const isChecklist = activeMode === CHECKLIST_MODE;
  const rows = (isChecklist ? unit?.currentChecklist : unit?.currentContents) || [];

  return (
    <ModalComponent
      title={`Current Content & Checklist${
        unit?.unitCode ? ` - ${unit?.unitCode}` : ''
      }`}
      open={open}
      onCancel={onClose}
      footer={null}
      className="modal inventory-unit-contents-modal"
      width={isDesktop ? 1000 : '95%'}
      destroyOnClose
    >
      <div className="inventory-unit-contents">
        <div className="inventory-unit-contents-switch">
          <Radio.Group
            size="large"
            buttonStyle="solid"
            optionType="button"
            value={activeMode}
            onChange={(event) => setActiveMode(event?.target?.value)}
          >
            <Radio.Button value={CONTENTS_MODE}>
              {`Content (${unit?.currentContents?.length || 0})`}
            </Radio.Button>
            <Radio.Button value={CHECKLIST_MODE}>
              {`Checklist (${unit?.currentChecklist?.length || 0})`}
            </Radio.Button>
          </Radio.Group>
        </div>
        <CommonTable
          columns={isChecklist ? checklistColumns : contentsColumns}
          data={rows}
          fullHeight={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 420px)' }}
          size="small"
          rowKey={(record) => record?.id}
          locale={{
            emptyText: isChecklist
              ? 'No checklist recorded yet'
              : 'No contents recorded yet',
          }}
        />
      </div>
    </ModalComponent>
  );
};

export default InventoryUnitContentsModal;
